import { AnimatePresence, motion } from 'framer-motion';
import '../../../styles/pages/tara/history/taraHistorySidebar.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import truncate from '../../../lib/helper-functions/truncate';
import { arrowHeadRightSVG } from '../../../lib/allCustomSVGs';
import {
  setAllChatsHistory,
  setCurrentChat,
  setIsBotTyping,
} from '../../../store/reducers/chatSlice';
import { useState } from 'react';
import { ChatMetadata } from '../../../interfaces/taraInterfaces';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';

interface Props {
  isSidebarMinimized: boolean;
}

const TaraHistorySidebar = ({ isSidebarMinimized }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const allChatsHistory = useSelector(
    (state: RootState) => state.chat.allChatsHistory
  );
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const historyLoading = useSelector(
    (state: RootState) => state.chat.historyLoading
  );

  const isChatHistory = allChatsHistory?.length > 0;
  const [dropdownStates, setDropdownStates] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleDropdown = (chatId: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDropdownStates((prevState) => ({
      ...prevState,
      [chatId]: !prevState[chatId],
    }));
  };

  const onHistoryItemClick = (chatMeta: ChatMetadata) => {
    if (currentPath !== '/tara') {
      const chatRoute = '/tara/chat/' + chatMeta.chatId;
      navigate(chatRoute);
    }

    // Avoid setting if it's already the active chat
    if (currentChat?.chatId === chatMeta.chatId) return;

    dispatch(
      setCurrentChat({
        chatId: chatMeta.chatId,
        chatStartedDate: chatMeta.chatStartedDate,
        chatLastUpdatedDate: chatMeta.chatLastUpdatedDate,
        chatMessages: [], // Placeholder, actual messages load separately
      })
    );
    dispatch(setIsBotTyping(false));
  };

  const onDeleteHistoryItem = (chatId: string | number) => {
    const updatedChatsHistory = allChatsHistory?.filter(
      (chat) => chat?.chatId !== chatId
    );
    dispatch(setAllChatsHistory(updatedChatsHistory));
    if (currentChat?.chatId === chatId) {
      dispatch(setCurrentChat(null));
      dispatch(setIsBotTyping(false));
    }
  };

  const renderHistoryItems = () => {
    return (
      <div className='taraHistorySidebar__itemContainer'>
        {!isChatHistory ? (
          <div className='taraHistorySidebar--empty'>{t('NoChatHistory')}</div>
        ) : (
          <AnimatePresence>
            {allChatsHistory.slice(0, 5).map((chatMeta, i) => {
              const historyItemTitle = truncate(chatMeta.chatTitle, 25) || '';
              return (
                <motion.li
                  key={`${chatMeta.chatId}-${i}`}
                  onClick={() => onHistoryItemClick(chatMeta)}
                  title={historyItemTitle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  data-sub_page_link_holder={
                    chatMeta.chatId === currentChat?.chatId &&
                    currentPath.includes('tara/chat')
                      ? 'true'
                      : 'false'
                  }
                  className='taraHistorySidebar--linkItem'
                  style={{ cursor: 'pointer', padding: '0.5rem' }}
                >
                  <div className='taraHistorySidebar--linkItem-left'>
                    <span style={{ display: 'block' }}>{historyItemTitle}</span>

                    {/* Dropdown (hidden until backend deletion is ready) */}
                    {/* <div
                      className='taraHistorySidebar--linkItem-left-svg'
                      onClick={(e) => toggleDropdown(chatMeta.chatId, e)}
                    >
                      <CustomSVGs svg={menuDotsVerticalSVG} />
                      {dropdownStates[chatMeta.chatId] && (
                        <TaraHistoryItemDropdown
                          onListItemClick={() => onDeleteHistoryItem(chatMeta.chatId)}
                          dropdownId={`dropdown-${chatMeta.chatId}`}
                        />
                      )}
                    </div> */}
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        )}
        {allChatsHistory.length > 0 && (
          <div
            onClick={() => navigate('/tara/search')}
            className='taraHistorySidebar--viewAll'
          >
            <span>{t('ViewAll')}</span>
            <CustomSVGs svg={arrowHeadRightSVG} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      data-is-sidebar-minimized-history={isSidebarMinimized.toString()}
      className='taraHistorySidebar'
    >
      {historyLoading === 'pending' ? <LoadingSpinner /> : renderHistoryItems()}
    </div>
  );
};

export default TaraHistorySidebar;
